import { useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import type {
  FrontendToolDefinition,
  FrontendToolResult,
} from '@/hooks/useFrontendTool';
import type { RegisterStepTool } from './types';

export interface DynamicStepToolOptions<T> {
  /** Stable tool name exposed to the agent, e.g. `configure_managed_mapper_step`. */
  name: string;
  /** Agent-facing description of what setting this value does. */
  description: string;
  /**
   * Zod schema for the tool's single argument; its parsed value becomes the new
   * state. Compared by emitted JSON Schema content, so an inline schema is fine.
   */
  schema: z.ZodType<T>;
  /** Name of the tool's argument (defaults to `value`). */
  argName?: string;
  /** Bubbled from `ConfigRendererProps`; undefined in read-only/overview contexts. */
  registerStepTool?: RegisterStepTool;
}

/**
 * `useState` for a config field that doubles as a Nami tool.
 *
 * Drop-in for `useState`: returns `[value, setValue]`, where `setValue` runs the
 * `apply` side effect the renderer already performs on change (e.g. an
 * `onConfigChange` that regenerates sockets). It additionally registers an
 * awaitable frontend tool so the agent can set the same value: the tool runs the
 * same `apply` and returns whatever it resolves to (e.g. the regenerated
 * sockets), so the agent sees the result in the same turn.
 *
 * The tool is only registered while the renderer is mounted — i.e. while the
 * step's config panel is open. The agent opens a step with `select_step` first.
 *
 * ```ts
 * const [agentDefinitionId, setAgentDefinitionId] = useDynamicStepState(
 *   typeof config.agentDefinitionId === 'string' ? config.agentDefinitionId : '',
 *   (id) => onConfigChange({ ...config, agentDefinitionId: id }),
 *   { name: 'configure_background_agent_step', description, schema: z.string().min(1), argName: 'agentDefinitionId', registerStepTool },
 * );
 * ```
 */
export function useDynamicStepState<T>(
  initialValue: T,
  apply: (value: T) => void | Promise<unknown>,
  options: DynamicStepToolOptions<T>,
): [T, (value: T) => void] {
  const [value, setValue] = useState(initialValue);

  const applyRef = useRef(apply);

  // No useCallback / useMemo anywhere in this hook: React Compiler memoizes
  // both this setter and the tool below.
  const setAndApply = (next: T) => {
    setValue(next);
    void applyRef.current(next);
  };

  const { name, description, schema, registerStepTool } = options;
  const argName = options.argName ?? 'value';

  // The tool depends on the emitted JSON Schema's *content*, not the Zod
  // instance: the compiler compares memo inputs with Object.is, and equal
  // strings are Object.is-equal, so an inline `schema` still yields a stable
  // tool and the registration effect below doesn't loop.
  const parametersJson = JSON.stringify(
    z.toJSONSchema(z.object({ [argName]: schema })),
  );
  const schemaRef = useRef(schema);

  // Latest `apply`/`schema` held in refs so the tool identity stays stable.
  // Written in an effect, not during render, so render stays pure.
  useEffect(() => {
    applyRef.current = apply;
    schemaRef.current = schema;
  });

  // Kept despite react-compiler-no-manual-memoization: the compiler would key
  // this on the upstream `schema` (Zod instance), not on the derived
  // `parametersJson`, so an inline schema would churn the tool identity and
  // loop the registration effect below. Same reasoning as useFrontendTool.
  // execute reads the latest `apply`/`schema` via ref so it never goes stale.
  const tool = useMemo<FrontendToolDefinition>(() => {
    const execute = async (
      rawArgs: Record<string, unknown>,
    ): Promise<FrontendToolResult> => {
      const parsed = schemaRef.current.safeParse(rawArgs?.[argName]);
      if (!parsed.success) {
        return {
          ok: false,
          error: `Invalid ${argName}: ${parsed.error.message}`,
        };
      }
      setValue(parsed.data);
      const result = await applyRef.current(parsed.data);
      return { ok: true, data: { [argName]: parsed.data, result } };
    };
    return {
      name,
      description,
      awaitable: true,
      enabled: true,
      schema: {
        name,
        description,
        parameters: JSON.parse(parametersJson) as Record<string, unknown>,
        awaitable: true,
      },
      execute: execute as FrontendToolDefinition['execute'],
    };
  }, [name, description, argName, parametersJson]);

  useEffect(() => {
    registerStepTool?.(name, tool);
    return () => registerStepTool?.(name, null);
  }, [registerStepTool, name, tool]);

  return [value, setAndApply];
}
